import React from 'react';
import {
  FaFileAlt,
  FaMoneyBillWave,
  FaCoins,
  FaShoppingCart,
  FaClock,
  FaClipboardCheck,
  FaHandHoldingUsd,
  FaBuilding,
  FaChartLine
} from 'react-icons/fa';
import {
  MdAccountBalance,
  MdAdminPanelSettings,
  MdAssignment,
  MdAttachMoney,
  MdBusiness,
  MdCampaign,
  MdComputer,
  MdContactMail,
  MdCorporateFare,
  MdDashboard,
  MdDescription,
  MdEvent,
  MdExtension,
  MdGroup,
  MdHome,
  MdHowToReg,
  MdPeople,
  MdPeopleAlt,
  MdSecurity,
  MdSettings,
  MdTask,
  MdTimer,
  MdTrendingUp,
  MdBusinessCenter
} from 'react-icons/md';

// Service interface
interface Service {
  header: string;
  service_id: string;
  icon?: string;
  PrimaryRender: boolean;
  route?: string;
  description?: string;
}

// NavigationItem interface
export interface NavigationItem {
  path: string;
  name: string;
  icon?: React.ReactElement;
  serviceTypes?: string[];
  serviceId?: string;
  serviceName?: string;
  requiresClient?: boolean;
  requiresAdmin?: boolean;
  customComponent?: string;
  requiresPlatformAdmin?: boolean;
  isExpandable?: boolean;
  isSubSection?: boolean;
  children?: NavigationItem[];
  route?: string;
  isDynamicService?: boolean;
}

// Service types
export const SERVICE_TYPES = {
  NDIS: 'NDIS Services',
  PAYROLL: 'Payroll Management',
  PEOPLE_SCREENING: 'People Screening',
  ECOMMERCE: 'E-commerce',
  COMMUNITY: 'Community Services',
  HEALTHCARE: 'Healthcare',
  FINANCE: 'Financial Services',
  BUSINESS_TOOLS: 'Business Tools'
} as const;

// All navigation items with Fund Management Investment as static (all admin options)
export const navigationItems: NavigationItem[] = [
  // Home/Services
  {
    path: '/services',
    name: 'Home/Services',
    icon: <MdHome />,
    customComponent: 'dual-action-buttons'
  },

  // NDIS Business Tools
  {
    path: '/profile',
    name: 'NDIS Business Tools',
    icon: <MdExtension />,
    requiresPlatformAdmin: true,
    route: '/profile',
    children: [
      {
        path: '/ndis-operations',
        name: 'NDIS Operations',
        icon: <MdAssignment />,
        requiresPlatformAdmin: true,
        isSubSection: true,
        children: [
          {
            path: '/ndis-operations/people-culture',
            name: 'People & Culture',
            icon: <MdPeopleAlt />,
            requiresPlatformAdmin: true
          },
          {
            path: '/ndis-operations/corporate-services',
            name: 'Corporate Services',
            icon: <MdCorporateFare />,
            requiresPlatformAdmin: true
          },
          {
            path: '/ndis-operations/information-technology',
            name: 'Information Technology',
            icon: <MdComputer />,
            requiresPlatformAdmin: true
          },
          {
            path: '/ndis-operations/finance',
            name: 'Finance',
            icon: <MdAttachMoney />,
            requiresPlatformAdmin: true,
            requiresClient: true
          },
          {
            path: '/ndis-operations/risk-compliance-governance',
            name: 'Risk & Compliance',
            icon: <MdSecurity />,
            requiresPlatformAdmin: true
          },
          {
            path: '/ndis-operations/marketing-engagement',
            name: 'Marketing & Engagement',
            icon: <MdCampaign />,
            requiresPlatformAdmin: true
          }
        ]
      },
      {
        path: '/clockInOut',
        name: 'Clock In/Clock Out',
        icon: <MdTimer />,
        requiresPlatformAdmin: true,
        requiresClient: true
      },
      {
        path: '/taskboard/board',
        name: 'Task Board',
        icon: <MdTask />,
        requiresPlatformAdmin: true,
        requiresClient: true
      },
      {
        path: '/CalenderShifts',
        name: 'Calendar Shifts',
        icon: <MdEvent />,
        requiresPlatformAdmin: true,
        requiresClient: true
      }
    ]
  },

  // Platform Admin
  {
    path: '/platform-admin',
    name: 'Platform Admin',
    icon: <MdDashboard />,
    requiresPlatformAdmin: true,
    route: 'platform-admin',
    children: [
      {
        path: '/platform-admin/groups',
        name: 'Groups',
        icon: <MdGroup />,
        requiresPlatformAdmin: true
      },
      {
        path: '/platform-admin/services',
        name: 'Services',
        icon: <MdSettings />,
        requiresPlatformAdmin: true
      },
      {
        path: '/platform-admin/members',
        name: 'Members',
        icon: <MdPeople />,
        requiresPlatformAdmin: true
      },
      {
        path: '/platform-admin/roles',
        name: 'Roles',
        icon: <MdAdminPanelSettings />,
        requiresPlatformAdmin: true
      },
      {
        path: '/platform-admin/contact-submissions',
        name: 'Contact Submissions',
        icon: <MdContactMail />,
        requiresPlatformAdmin: true
      }
    ]
  },

  // Budgeting Tools
  {
    path: '/income-expenses',
    name: 'Budgeting Tools',
    icon: <MdTrendingUp />,
    requiresPlatformAdmin: true,
    route: 'income-expenses',
    children: [
      {
        path: '/income-expenses',
        name: 'Income and Expenses',
        icon: <MdAccountBalance />,
        requiresPlatformAdmin: true
      }
    ]
  },

  // Payroll/People Screening
  {
    path: '/payroll',
    name: 'People Screening',
    icon: <MdPeople />,
    requiresPlatformAdmin: true,
    route: 'payroll',
    children: [
      {
        path: '/payroll/people',
        name: 'People',
        icon: <MdPeople />,
        requiresPlatformAdmin: true
      },
      {
        path: '/payroll/documents',
        name: 'Documents',
        icon: <MdDescription />,
        requiresPlatformAdmin: true
      },
      {
        path: '/ndis-operations/corporate-services',
        name: 'Organisation',
        icon: <MdBusiness />,
        requiresPlatformAdmin: true
      },
      {
        path: '/payroll/administration',
        name: 'Administration',
        icon: <MdSettings />,
        requiresPlatformAdmin: true,
        isSubSection: true,
        children: [
          {
            path: '/payroll/onboarding',
            name: 'Onboarding',
            icon: <MdHowToReg />,
            requiresPlatformAdmin: true
          }
        ]
      }
    ]
  },

  // Fund Management Investment - Full admin menu
  {
    path: '/fundManagementInvestment',
    name: 'Fund Management Investment',
    icon: <FaCoins />,
    requiresPlatformAdmin: true,
    route: 'fundManagementInvestment',
    children: [
      {
        path: '/fundManagementInvestment/dashboard',
        name: 'Dashboard',
        icon: <MdDashboard />,
        requiresPlatformAdmin: true
      },
      {
        path: '/fundManagementInvestment/purchase-units',
        name: 'Purchase Units',
        icon: <FaCoins />,
        requiresPlatformAdmin: true
      },
      {
        path: '/fundManagementInvestment/sell-units',
        name: 'Sell Units',
        icon: <FaMoneyBillWave />,
        requiresPlatformAdmin: true
      },
      {
        path: '/fundManagementInvestment/purchase-assets',
        name: 'Purchase Assets',
        icon: <MdBusinessCenter />,
        requiresPlatformAdmin: true
      },
      {
        path: '/fundManagementInvestment/sell-assets',
        name: 'Sell Assets',
        icon: <FaHandHoldingUsd />,
        requiresPlatformAdmin: true
      },
      {
        path: '/fundManagementInvestment/request',
        name: 'Approval Requests',
        icon: <FaClipboardCheck />,
        requiresPlatformAdmin: true
      },
      {
        path: '/fundManagementInvestment/all-assets',
        name: 'Assets List',
        icon: <FaBuilding />,
        requiresPlatformAdmin: true
      },
      {
        path: '/fundManagementInvestment/reports',
        name: 'Reports',
        icon: <FaFileAlt />,
        requiresPlatformAdmin: true
      },
      {
        path: '/fundManagementInvestment/simulation',
        name: 'Simulation',
        icon: <FaChartLine />,
        requiresPlatformAdmin: true
      }
    ]
  }
];

// Simple function to filter Fund Management menu based on admin rights
export const filterFundManagementMenu = (
  items: NavigationItem[],
  fundManagementAdminRight: boolean
): NavigationItem[] => {
  return items.map((item) => {
    // Only modify Fund Management Investment menu
    if (item.path === '/fundManagementInvestment' && !fundManagementAdminRight) {
      return {
        ...item,
        children: [
          {
            path: '/fundManagementInvestment/purchase-units',
            name: 'Purchase Units',
            icon: <FaShoppingCart />,
            requiresPlatformAdmin: true
          },
          {
            path: '/fundManagementInvestment/request',
            name: 'Request Status',
            icon: <FaClock />,
            requiresPlatformAdmin: true
          },
          {
            path: '/fundManagementInvestment/reports',
            name: 'Reports',
            icon: <FaFileAlt />,
            requiresPlatformAdmin: true
          }
        ]
      };
    }
    return item;
  });
};

// SERVICE FILTERING FUNCTION
export const filterNavigationByServices = (
  items: NavigationItem[],
  services: Service[]
): NavigationItem[] => {
  if (!services || services.length === 0) {
    return items.filter(
      (item) => item.customComponent === 'dual-action-buttons' || item.path === '/services'
    );
  }

  const serviceRoutes = services
    .map((service) => service.route)
    .filter(
      (route): route is string => route !== undefined && route !== null && route.trim().length > 0
    )
    .map((route) => (route.startsWith('/') ? route.slice(1) : route));

  return items.filter((item) => {
    if (item.customComponent === 'dual-action-buttons' || item.path === '/services') {
      return true;
    }

    const itemRoute = item.route;
    if (!itemRoute) return false;

    const normalizedItemRoute = itemRoute.startsWith('/') ? itemRoute.slice(1) : itemRoute;

    return serviceRoutes.some((serviceRoute) => {
      return (
        serviceRoute === normalizedItemRoute ||
        serviceRoute === itemRoute ||
        `/${serviceRoute}` === itemRoute ||
        serviceRoute === `/${normalizedItemRoute}`
      );
    });
  });
};

// PERMISSION CHECK FUNCTION - Required by DrawerContent
export const canAccessMenuItem = (
  item: NavigationItem,
  hasClient: boolean,
  isServiceAdmin: boolean,
  isPlatformAdmin: boolean
): boolean => {
  if (item.requiresPlatformAdmin && !isPlatformAdmin) return false;
  if (item.requiresAdmin && !isServiceAdmin && !isPlatformAdmin) return false;
  if (item.requiresClient && !hasClient) return false;
  return true;
};

// PERMISSION FILTERING FUNCTION
export const filterNavigationByPermissions = (
  items: NavigationItem[],
  hasClient: boolean,
  isServiceAdmin: boolean,
  isPlatformAdmin: boolean
): NavigationItem[] => {
  return items
    .map((item) => ({
      ...item,
      children: item.children
        ? filterNavigationByPermissions(item.children, hasClient, isServiceAdmin, isPlatformAdmin)
        : []
    }))
    .filter((item) => canAccessMenuItem(item, hasClient, isServiceAdmin, isPlatformAdmin));
};

// MAIN FUNCTION TO GET NAVIGATION WITH FUND MANAGEMENT FILTERING
export const getNavigationItems = (
  fundManagementAdminRight: boolean = true // Default to admin view
): NavigationItem[] => {
  return filterFundManagementMenu(navigationItems, fundManagementAdminRight);
};

// FOR HOMETILES - Required by HomeTiles component
export const generateDynamicServiceMenus = (services: Service[]): NavigationItem[] => {
  console.log('📋 Services available for dynamic menus:', services);
  return [];
};

// Helper functions
export const getNavigationItemByPath = (path: string): NavigationItem | undefined => {
  const findItem = (items: NavigationItem[]): NavigationItem | undefined => {
    for (const item of items) {
      if (item.path === path) return item;
      if (item.children) {
        const found = findItem(item.children);
        if (found) return found;
      }
    }
    return undefined;
  };
  return findItem(navigationItems);
};

export const getAllNavigationPaths = (items: NavigationItem[] = navigationItems): string[] => {
  const paths: string[] = [];

  const collectPaths = (navigationItems: NavigationItem[]) => {
    navigationItems.forEach((item) => {
      paths.push(item.path);
      if (item.children) {
        collectPaths(item.children);
      }
    });
  };

  collectPaths(items);
  return paths;
};

export const hasChildren = (path: string, items: NavigationItem[] = navigationItems): boolean => {
  const item = getNavigationItemByPath(path);
  return item ? Boolean(item.children && item.children.length > 0) : false;
};

export const getChildrenByPath = (
  path: string,
  items: NavigationItem[] = navigationItems
): NavigationItem[] => {
  const item = getNavigationItemByPath(path);
  return item?.children || [];
};

export const getParentPath = (
  childPath: string,
  items: NavigationItem[] = navigationItems,
  parentPath?: string
): string | undefined => {
  for (const item of items) {
    if (item.children) {
      if (item.children.some((child) => child.path === childPath)) {
        return item.path;
      }
      const found = getParentPath(childPath, item.children, item.path);
      if (found) return found;
    }
  }
  return undefined;
};

export const getBreadcrumbChain = (
  currentPath: string,
  items: NavigationItem[] = navigationItems
): Array<{ path: string; name: string }> => {
  const chain: Array<{ path: string; name: string }> = [];

  const findPath = (
    targetPath: string,
    navigationItems: NavigationItem[],
    currentChain: Array<{ path: string; name: string }> = []
  ): boolean => {
    for (const item of navigationItems) {
      const newChain = [...currentChain, { path: item.path, name: item.name }];

      if (item.path === targetPath) {
        chain.push(...newChain);
        return true;
      }

      if (item.children && findPath(targetPath, item.children, newChain)) {
        return true;
      }
    }
    return false;
  };

  findPath(currentPath, items);
  return chain;
};

// HELPER FUNCTIONS FOR NDIS SERVICE DETECTION
export const isNDISService = (serviceName: string | null | undefined): boolean => {
  if (!serviceName) return false;
  const serviceNameLower = serviceName.toLowerCase();
  return (
    serviceNameLower.includes('ndis') || serviceNameLower.includes('national disability insurance')
  );
};

// SERVICE MENU CLICK HANDLER INTERFACE
export interface ServiceMenuClickHandler {
  (serviceId: string, serviceName: string, menuPath: string): void;
}

// CUSTOM HOOK FOR SERVICE MENU HANDLING
export const useServiceMenuHandler = (
  dispatch: any,
  navigate: any,
  setSelectedService: any,
  setSelectedServiceTitle: any
) => {
  const handleServiceMenuClick: ServiceMenuClickHandler = (
    serviceId: string,
    serviceName: string,
    menuPath: string
  ) => {
    console.log('Service menu clicked:', { serviceId, serviceName, menuPath });

    // Update selected service (same as clicking service cards)
    dispatch(
      setSelectedService({
        title: serviceName,
        id: serviceId
      })
    );
    dispatch(setSelectedServiceTitle(serviceName));

    // Navigate to the service
    navigate(menuPath);
  };

  return { handleServiceMenuClick };
};

// Make this a module
export {};
